import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  CircularProgress,
  useTheme,
  Button,
  Tooltip,
} from '@mui/material';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { Chart, registerables, ChartDataset } from 'chart.js';
import { Chart as ReactChart } from 'react-chartjs-2';
import FilterSelect from '@Components/Filter/Dropdown';
import { RootStateType } from '@Store';
import { DropdownOptionType } from '@Components/Dropdown/Type';
import API from '@Services/apiAxios';
import { formatNumber, useLocale } from '../../utils/formatNumber';

// ─── Chart Type Definition ────────────────────────────────────────────────────
type ChartType = 'line' | 'bar' | 'mixed' | 'sentiment';

interface ChartTypeOption {
  value: ChartType;
  label: string;
  icon: string;
  tooltip: string;
}

const chartTypeOptions: ChartTypeOption[] = [
  {
    value: 'bar',
    label: 'Bar',
    icon: '▐▐',
    tooltip: 'Bar Chart – Invoice charges as grouped bars',
  },
  {
    value: 'line',
    label: 'Line',
    icon: '〰',
    tooltip: 'Line Chart – All metrics as smooth lines',
  },
  {
    value: 'mixed',
    label: 'Mixed',
    icon: '⚡',
    tooltip: 'Mixed Chart – Bars for invoices, line for profit/weight/shipment',
  },
  {
    value: 'sentiment',
    label: 'Sentiment',
    icon: '◕',
    tooltip: 'Sentiment Trend – Positive (green) vs Negative (red) gross profit zones',
  },
];

// ─── Custom Plugins ───────────────────────────────────────────────────────────
const crosshairWithValue = {
  id: 'crosshairWithValue',
  afterDraw: (chart: any) => {
    if (chart.tooltip?._active?.length) {
      const ctx = chart.ctx;
      const activePoint = chart.tooltip._active[0];
      const x = activePoint.element.x;
      const y = activePoint.element.y;
      const topY = chart.scales.y.top;
      const bottomY = chart.scales.y.bottom;
      const leftX = chart.scales.x.left;
      const rightX = chart.scales.x.right;
      const value = activePoint.element.$context.raw;

      ctx.save();
      ctx.setLineDash([4, 4]);
      ctx.lineWidth = 1;
      ctx.strokeStyle = '#94A3B8';
      ctx.beginPath();
      ctx.moveTo(x, topY);
      ctx.lineTo(x, bottomY);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(leftX, y);
      ctx.lineTo(rightX, y);
      ctx.stroke();
      ctx.setLineDash([]);

      const boxWidth = 50;
      const boxHeight = 20;
      const padding = 5;
      ctx.fillStyle = '#0F172A';
      ctx.fillRect(rightX + padding, y - boxHeight / 2, boxWidth, boxHeight);
      ctx.fillStyle = '#fff';
      ctx.font = '12px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(`${value}`, rightX + padding + boxWidth / 2, y);
      ctx.restore();
    }
  },
};

const hoverLine = {
  id: 'hoverLine',
  afterDraw: (chart: any) => {
    if (chart.tooltip?._active?.length) {
      const ctx = chart.ctx;
      const activePoint = chart.tooltip._active[0];
      const x = activePoint.element.x;
      const topY = chart.scales.y.top;
      const bottomY = chart.scales.y.bottom;
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(x, topY);
      ctx.lineTo(x, bottomY);
      ctx.lineWidth = 1;
      ctx.strokeStyle = '#CBD5F5';
      ctx.stroke();
      ctx.restore();
    }
  },
};

Chart.register(...registerables);
Chart.register(hoverLine);
Chart.register(crosshairWithValue);

// ─── Month Options ────────────────────────────────────────────────────────────
const generateMonthOptions = (): DropdownOptionType[] => {
  return Array.from({ length: 6 }, (_, i) => {
    const date = moment().subtract(i, 'months');
    return {
      value: date.format('MM-YYYY'),
      display: date.format('MMMM YYYY'),
    };
  });
};

const monthOptions = generateMonthOptions();

// ─── Types ────────────────────────────────────────────────────────────────────
interface GraphDataItem {
  month: string;
  year: string;
  result: {
    sellingInvoiceCharges: number;
    buyingInvoiceCharges: number;
    grossProfit: number;
    totalWeight: number;
    totalShipment: number;
  };
}

interface ApiResponse {
  msg: string;
  data: {
    graphData: GraphDataItem[];
  };
}

// ─── Component ────────────────────────────────────────────────────────────────
const GrossProfitGraph = () => {
  const theme = useTheme();
  const locale = useLocale();
  const { loggedInUserSystemDropdownList } = useSelector((state: RootStateType) => state.System);

  const [system, setSystem] = useState<string[]>([]);
  const [graphData, setGraphData] = useState<GraphDataItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [dimension, setDimension] = useState<'revenue' | 'weight' | 'shipment'>('revenue');
  const [chartType, setChartType] = useState<ChartType>('mixed');
  const [selectedValues, setSelectedValues] = useState<{ month: string; year: string }>({
    month: moment().format('MM'),
    year: moment().format('YYYY'),
  });

  const handleChange = (event: SelectChangeEvent<`${string}-${string}`>) => {
    const [month, year] = event.target.value.split('-');
    setSelectedValues({ month, year });
  };

  const onSystemFilter = (selected: Array<string>) => {
    setSystem(selected);
  };

  const fetchGrossProfitData = async () => {
    setLoading(true);
    try {
      const res = await API.get<ApiResponse>(`/analytics/stats/grossprofitmonthly-invoiced`, {
        params: {
          month: selectedValues.month,
          year: selectedValues.year,
          dimension,
          system,
        },
      });
      setGraphData(res.data.data.graphData);
    } catch (error) {
      console.error('Failed to fetch gross profit data', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGrossProfitData();
  }, [selectedValues, system]);

  // ─── Dynamic Datasets based on chart type ──────────────────────────────────
  const getDynamicData = () =>
    graphData.map(item => {
      switch (dimension) {
        case 'revenue':
          return item.result.grossProfit;
        case 'weight':
          return item.result.totalWeight;
        case 'shipment':
          return item.result.totalShipment;
        default:
          return 0;
      }
    });

  const getDynamicLabel = () => {
    switch (dimension) {
      case 'revenue':
        return 'Gross Profit';
      case 'weight':
        return 'Total Weight';
      case 'shipment':
        return 'Total Shipment';
      default:
        return 'Value';
    }
  };

  const getDynamicColor = () => {
    switch (dimension) {
      case 'revenue':
        return theme.palette.success.main;
      case 'weight':
        return theme.palette.info.main;
      case 'shipment':
        return theme.palette.warning.main;
      default:
        return theme.palette.primary.main;
    }
  };

  const getDatasets = (): ChartDataset<'bar' | 'line', number[]>[] => {
    const dynamicData = getDynamicData();
    const dynamicColor = getDynamicColor();
    const dynamicLabel = getDynamicLabel();

    // ── LINE CHART: All metrics as lines ──────────────────────────────────────
    if (chartType === 'line') {
      return [
        {
          label: 'Selling Invoice Charges',
          type: 'line' as const,
          data: graphData.map(item => item.result.sellingInvoiceCharges),
          borderColor: theme.palette.primary.main,
          backgroundColor: `${theme.palette.primary.main}20`,
          borderWidth: 2,
          // fill: true,
          tension: 0.4,
          yAxisID: 'y',
          pointRadius: 4,
          pointBackgroundColor: theme.palette.primary.main,
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          pointHoverRadius: 7,
        },
        {
          label: 'Buying Invoice Charges',
          type: 'line' as const,
          data: graphData.map(item => item.result.buyingInvoiceCharges),
          borderColor: theme.palette.secondary.main,
          backgroundColor: `${theme.palette.secondary.main}20`,
          borderWidth: 2,
          // fill: true,
          tension: 0.4,
          yAxisID: 'y',
          pointRadius: 4,
          pointBackgroundColor: theme.palette.secondary.main,
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          pointHoverRadius: 7,
        },
        {
          label: dynamicLabel,
          type: 'line' as const,
          data: dynamicData,
          borderColor: dynamicColor,
          backgroundColor: `${dynamicColor}15`,
          borderWidth: 3,
          fill: false,
          tension: 0.3,
          yAxisID: 'y1',
          pointRadius: (ctx: any) => (ctx.raw < 0 ? 6 : 4),
          pointBackgroundColor: (ctx: any) => (ctx.raw < 0 ? theme.palette.error.main : dynamicColor),
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          pointHoverRadius: 8,
        },
      ];
    }

    // ── BAR CHART: All metrics as bars ────────────────────────────────────────
    if (chartType === 'bar') {
      return [
        {
          label: 'Selling Invoice Charges',
          type: 'bar' as const,
          data: graphData.map(item => item.result.sellingInvoiceCharges),
          backgroundColor: theme.palette.primary.main,
          borderColor: theme.palette.primary.main,
          borderWidth: 1,
          borderRadius: 6,
          yAxisID: 'y',
          barThickness: 40,
          stack: 'selling',
        },
        {
          label: 'Buying Invoice Charges',
          type: 'bar' as const,
          data: graphData.map(item => item.result.buyingInvoiceCharges),
          backgroundColor: theme.palette.secondary.main,
          borderColor: theme.palette.secondary.main,
          borderWidth: 1,
          borderRadius: 6,
          yAxisID: 'y',
          barThickness: 40,
          stack: 'buying',
        },
        {
          label: dynamicLabel,
          type: 'bar' as const,
          data: dynamicData,
          backgroundColor: (ctx: any) => (ctx.raw < 0 ? `${theme.palette.error.main}CC` : `${dynamicColor}CC`),
          borderColor: (ctx: any) => (ctx.raw < 0 ? theme.palette.error.main : dynamicColor),
          borderWidth: 1,
          borderRadius: 6,
          yAxisID: 'y1',
          barThickness: 40,
          stack: 'dynamic',
        },
      ];
    }

    // ── SENTIMENT TREND CHART ─────────────────────────────────────────────────
    if (chartType === 'sentiment') {
      const profitData = graphData.map(item => item.result.grossProfit);
      return [
        {
          label: 'Positive Profit',
          type: 'bar' as const,
          data: profitData.map(v => (v >= 0 ? v : 0)),
          backgroundColor: `${theme.palette.success.main}CC`,
          borderColor: theme.palette.success.main,
          borderWidth: 1,
          borderRadius: 6,
          yAxisID: 'y',
          barThickness: 40,
          stack: 'profit',
        },
        {
          label: 'Negative Profit',
          type: 'bar' as const,
          data: profitData.map(v => (v < 0 ? v : 0)),
          backgroundColor: `${theme.palette.error.main}CC`,
          borderColor: theme.palette.error.main,
          borderWidth: 1,
          borderRadius: 6,
          yAxisID: 'y',
          barThickness: 40,
          stack: 'profit',
        },
        {
          label: 'Selling Invoice Charges',
          type: 'line' as const,
          data: graphData.map(item => item.result.sellingInvoiceCharges),
          borderColor: theme.palette.primary.main,
          backgroundColor: 'transparent',
          borderWidth: 2,
          fill: false,
          tension: 0.3,
          yAxisID: 'y1',
          pointRadius: 3,
          pointBackgroundColor: theme.palette.primary.main,
          pointBorderColor: '#fff',
          pointBorderWidth: 1,
          pointHoverRadius: 6,
          borderDash: [5, 3],
        },
        {
          label: 'Buying Invoice Charges',
          type: 'line' as const,
          data: graphData.map(item => item.result.buyingInvoiceCharges),
          borderColor: theme.palette.secondary.main,
          backgroundColor: 'transparent',
          borderWidth: 2,
          fill: false,
          tension: 0.3,
          yAxisID: 'y1',
          pointRadius: 3,
          pointBackgroundColor: theme.palette.secondary.main,
          pointBorderColor: '#fff',
          pointBorderWidth: 1,
          pointHoverRadius: 6,
          borderDash: [5, 3],
        },
      ];
    }

    // ── MIXED CHART (default): Bars + Line ────────────────────────────────────
    return [
      {
        label: 'Selling Invoice Charges',
        type: 'bar' as const,
        data: graphData.map(item => item.result.sellingInvoiceCharges),
        backgroundColor: theme.palette.primary.main,
        borderColor: theme.palette.primary.main,
        borderWidth: 1,
        borderRadius: 8,
        yAxisID: 'y',
        order: 2,
        stack: 'selling',
        barThickness: 50,
      },
      {
        label: 'Buying Invoice Charges',
        type: 'bar' as const,
        data: graphData.map(item => item.result.buyingInvoiceCharges),
        backgroundColor: theme.palette.secondary.main,
        borderColor: theme.palette.secondary.main,
        borderWidth: 1,
        borderRadius: 8,
        yAxisID: 'y',
        order: 3,
        stack: 'buying',
        barThickness: 50,
      },
      {
        label: dynamicLabel,
        type: 'line' as const,
        data: dynamicData,
        borderColor: dynamicColor,
        backgroundColor: 'transparent',
        borderWidth: 3,
        fill: false,
        tension: 0.3,
        yAxisID: 'y1',
        pointRadius: (ctx: any) => (ctx.raw < 0 ? 6 : 4),
        pointBackgroundColor: (ctx: any) => (ctx.raw < 0 ? theme.palette.error.main : dynamicColor),
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointHoverRadius: 8,
        order: 1,
      },
    ];
  };

  const chartData: { labels: string[]; datasets: ChartDataset<'bar' | 'line', number[]>[] } = {
    labels: graphData.map(item => `${item.month} ${item.year}`),
    datasets: getDatasets(),
  };

  const getDynamicAxisLabel = () => {
    if (chartType === 'sentiment') return 'Gross Profit (Sentiment)';
    switch (dimension) {
      case 'revenue':
        return 'Gross Profit';
      case 'weight':
        return 'Total Weight (kg)';
      case 'shipment':
        return 'Total Shipment (units)';
      default:
        return 'Value';
    }
  };

  // Resolve primary chart type for ReactChart `type` prop
  const resolvedChartType = chartType === 'line' ? 'line' : 'bar';

  return (
    <Box
      sx={{
        borderRadius: '9px',
        border: '1px solid #F1F1F1',
        boxShadow: '0px 1px 4px 0px rgba(0, 0, 0, 0.25)',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        padding: { xs: '15px 20px', sm: '20px 40px' },
      }}
    >
      <Box
        sx={{
          color: 'rgba(24, 29, 37, 1)',
          fontSize: { xs: '20px', sm: '24px' },
          fontWeight: 600,
          lineHeight: '33.6px',
          mb: 2,
        }}
      >
        Gross Profit Margin
      </Box>

      {/* ── Chart Type Selector ─────────────────────────────────────────────── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 1,
          flexWrap: 'wrap',
        }}
      >
        <Box
          sx={{
            fontSize: '0.75rem',
            fontWeight: 600,
            color: theme.palette.text.secondary,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            mr: 1,
          }}
        >
          Chart Type
        </Box>
        {chartTypeOptions.map(option => {
          const isActive = chartType === option.value;
          return (
            <Tooltip key={option.value} title={option.tooltip} placement="top" arrow>
              <Button
                onClick={() => setChartType(option.value)}
                variant={isActive ? 'contained' : 'outlined'}
                size="small"
                sx={{
                  textTransform: 'none',
                  fontWeight: isActive ? 600 : 400,
                  fontSize: '0.8rem',
                  px: 1.5,
                  py: 0.5,
                  borderRadius: '8px',
                  minWidth: 'unset',
                  gap: 0.7,
                  color: isActive ? '#fff' : theme.palette.text.secondary,
                  backgroundColor: isActive ? theme.palette.primary.main : 'transparent',
                  borderColor: isActive ? theme.palette.primary.main : theme.palette.divider,
                  boxShadow: isActive ? `0 2px 8px ${theme.palette.primary.main}40` : 'none',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: isActive ? theme.palette.primary.dark : theme.palette.action.hover,
                    borderColor: theme.palette.primary.main,
                  },
                }}
              >
                <span style={{ fontSize: '0.7rem', opacity: 0.85 }}>{option.icon}</span>
                {option.label}
              </Button>
            </Tooltip>
          );
        })}
      </Box>

      {/* ── Dimension Buttons + Filters ──────────────────────────────────────── */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: 2,
          mt: 2,
        }}
      >
        {/* Dimension Buttons */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          {['revenue', 'weight', 'shipment'].map(item => {
            const isSelected = dimension === item;
            return (
              <Button
                key={item}
                onClick={() => setDimension(item as 'revenue' | 'weight' | 'shipment')}
                variant="contained"
                sx={{
                  textTransform: 'none',
                  minWidth: 80,
                  px: 2,
                  py: 0.5,
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  color: isSelected ? '#fff' : theme.palette.text.primary,
                  backgroundColor: isSelected ? theme.palette.primary.main : '#fff',
                  border: isSelected ? 'none' : `1px solid ${theme.palette.divider}`,
                  '&:hover': {
                    backgroundColor: isSelected ? theme.palette.primary.dark : theme.palette.action.hover,
                  },
                  borderRadius: '10px',
                  margin: '0 4px',
                  padding: '8px 24px',
                }}
              >
                {item.charAt(0).toUpperCase() + item.slice(1)}
              </Button>
            );
          })}
        </Box>

        {/* Filters */}
        <Box
          sx={{
            width: { xs: '100%', sm: '30%' },
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
            alignItems: 'center',
          }}
        >
          <Box sx={{ flex: 1 }}>
            <FormControl fullWidth>
              <InputLabel id="month-select-label">Select month</InputLabel>
              <Select
                labelId="month-select-label"
                id="month-select"
                value={`${selectedValues.month}-${selectedValues.year}`}
                onChange={handleChange}
                label="Select month"
              >
                {monthOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.display}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ flex: 1 }}>
            <FilterSelect
              id="system"
              label="Select system"
              options={loggedInUserSystemDropdownList}
              value={system}
              onChange={onSystemFilter}
            />
          </Box>
        </Box>
      </Box>

      {/* ── Chart ────────────────────────────────────────────────────────────── */}
      <Box sx={{ mt: 4, height: 440 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <ReactChart
            key={chartType} // remount on chart type change for clean animation
            type={resolvedChartType}
            data={chartData as any}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              interaction: {
                mode: 'index',
                intersect: false,
              },
              plugins: {
                legend: {
                  position: 'top',
                  align: 'end',
                  labels: {
                    usePointStyle: true,
                    pointStyle: 'circle',
                    padding: 18,
                    font: { size: 12 },
                  },
                },
                tooltip: {
                  backgroundColor: '#0F172A',
                  padding: 14,
                  titleColor: '#fff',
                  bodyColor: '#E2E8F0',
                  callbacks: {
                    label: context => {
                      const value = context.raw as number;
                      const label = context.dataset.label || '';
                      return `${label}: ${formatNumber(value, locale)}`;
                    },
                  },
                },
              },
              scales: {
                x: {
                  stacked: chartType !== 'line',
                  grid: { display: false },
                  ticks: { color: '#64748B' },
                },
                y: {
                  stacked: chartType !== 'line',
                  beginAtZero: true,
                  grid: { color: '#F1F5F9' },
                  title: {
                    display: true,
                    text: chartType === 'sentiment' ? 'Gross Profit' : 'Invoice Charges',
                  },
                },
                y1: {
                  position: 'right',
                  grid: { drawOnChartArea: false },
                  title: { display: true, text: getDynamicAxisLabel() },
                },
              },
              animation: { duration: 1100, easing: 'easeOutQuart' },
            }}
          />
        )}
      </Box>
    </Box>
  );
};

export default GrossProfitGraph;
